import "core-js/modules/es.array.push.js";
import "core-js/modules/es.array.reduce.js";
import "core-js/modules/es.set.difference.v2.js";
import "core-js/modules/es.set.intersection.v2.js";
import "core-js/modules/es.set.is-disjoint-from.v2.js";
import "core-js/modules/es.set.is-subset-of.v2.js";
import "core-js/modules/es.set.is-superset-of.v2.js";
import "core-js/modules/es.set.symmetric-difference.v2.js";
import "core-js/modules/es.set.union.v2.js";
import Vue from 'vue';
import { STATES } from '@/constants/patient';
import { REASSIGNMENT_DISABLED_IN_OPTIONS } from '@/constants/doctors';
import { REASSIGNMENT_TYPE } from '@/constants/ooo';
import { DoctorService } from '@/services/doctor.service';
import IconLoading from '@/components/icon/IconLoading.vue';
import CoreTooltip from '@corefront/components-v2/Core/CoreTooltip.vue';
const STATE_NAME_MAP = Object.fromEntries(STATES.map(state => [state.code, state.statename]));
function splitByWeights(total, weights) {
  const sumWeights = weights.reduce((sum, w) => sum + w, 0);
  if (total <= 0 || sumWeights <= 0) {
    return weights.map(() => 0);
  }
  const raw = weights.map(w => total * w / sumWeights);
  const bases = raw.map(Math.floor);
  const remainder = total - bases.reduce((sum, b) => sum + b, 0);
  const order = raw.map((r, i) => ({
    i,
    frac: r - Math.floor(r)
  })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  const shares = [...bases];
  for (let k = 0; k < remainder && k < order.length; k++) {
    shares[order[k].i] += 1;
  }
  return shares;
}
export default {
  components: {
    IconLoading,
    CoreTooltip
  },
  props: {
    selectedStates: {
      type: Array,
      default: () => []
    },
    sourceDoctor: {
      type: Object,
      default: null
    },
    stateCounts: {
      type: Array,
      default: () => []
    },
    sourcePanelTotal: {
      type: Number,
      default: 0
    },
    reassignmentType: {
      type: String,
      default: REASSIGNMENT_TYPE.TEMPORARY
    },
    startTimestamp: {
      type: Number,
      default: null
    },
    endTimestamp: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      doctors: [],
      loading: false,
      fetchVersion: 0,
      selectedIds: [],
      doctorSearch: '',
      filters: [],
      pendingFilters: [],
      openDropdownIndex: null,
      includePatients: 'ALL',
      reduceByMode: 'COUNT',
      reduceByAmount: 0,
      amountDirty: false,
      weightByLoad: true,
      doctorScopes: {},
      scopePopoverDoctorId: null,
      manualOverrides: {},
      doctorLoads: {},
      loadingLoads: false
    };
  },
  computed: {
    eligibilityWindowKey() {
      return `${this.reassignmentType}|${this.startTimestamp}|${this.endTimestamp}`;
    },
    includeCaption() {
      return this.includePatients === 'ACTIVE' ? 'Reduce from active patients only.' : 'Reduce from the source doctor\'s full panel.';
    },
    columnOptions() {
      return [{
        value: 'state',
        text: 'State'
      }];
    },
    stateValueOptions() {
      return this.selectedStates.map(abbr => ({
        value: abbr,
        text: `${STATE_NAME_MAP[abbr] || abbr} (${abbr})`
      }));
    },
    stateCountsFiltered() {
      return this.selectedStates.map(abbr => {
        const entry = this.stateCounts.find(sc => sc.abbr === abbr) || {};
        const count = this.includePatients === 'ACTIVE' ? entry.active || 0 : entry.total || 0;
        return {
          abbr,
          count,
          ownedTotal: entry.owned || 0,
          coveringTotal: entry.covering || 0
        };
      });
    },
    totalIncludedCount() {
      return this.stateCountsFiltered.reduce((sum, s) => sum + s.count, 0);
    },
    includePatientsCounts() {
      return this.selectedStates.reduce((acc, abbr) => {
        const entry = this.stateCounts.find(sc => sc.abbr === abbr) || {};
        acc.total += entry.total || 0;
        acc.active += entry.active || 0;
        acc.cancelled += entry.cancelled || 0;
        return acc;
      }, {
        total: 0,
        active: 0,
        cancelled: 0
      });
    },
    ownedCoveringCounts() {
      return this.stateCountsFiltered.reduce((acc, s) => {
        const denom = s.ownedTotal + s.coveringTotal;
        const owned = denom > 0 ? Math.round(s.count * s.ownedTotal / denom) : s.count;
        acc.owned += owned;
        acc.covering += s.count - owned;
        return acc;
      }, {
        owned: 0,
        covering: 0
      });
    },
    reduceTargetCeiling() {
      return this.totalIncludedCount;
    },
    reduceTargetCount() {
      const raw = this.reduceByMode === 'PERCENT' ? Math.round(this.reduceByAmount / 100 * this.sourcePanelTotal) : this.reduceByAmount;
      return Math.max(0, Math.min(raw, this.reduceTargetCeiling));
    },
    reduceCaption() {
      if (this.reduceByMode === 'COUNT') {
        const pct = this.sourcePanelTotal > 0 ? Math.round(this.reduceTargetCount / this.sourcePanelTotal * 100) : 0;
        return `→ ${pct}% of panel`;
      }
      return `→ ${this.reduceTargetCount.toLocaleString()} patients`;
    },
    autoAllocation() {
      return this.computeAutoAllocation(this.manualOverrides);
    },
    baselineAllocation() {
      return this.computeAutoAllocation({});
    },
    totalDistributed() {
      return this.selectedIds.reduce((sum, id) => sum + this.getTotalEstimate(id), 0);
    },
    totalAssignedOwned() {
      return this.selectedIds.reduce((sum, id) => sum + this.getDoctorOwnedCovering(id).owned, 0);
    },
    totalAssignedCovering() {
      return this.selectedIds.reduce((sum, id) => sum + this.getDoctorOwnedCovering(id).covering, 0);
    },
    receivingDoctorsPayload() {
      return this.selectedIds.map(id => {
        const {
          owned,
          covering
        } = this.getDoctorOwnedCovering(id);
        return {
          doctorId: id,
          activeStates: this.doctorScopes[id] || [],
          isManualOverride: id in this.manualOverrides,
          allocation: {
            total: owned + covering,
            owned,
            covering
          }
        };
      }).filter(entry => entry.allocation.total > 0);
    },
    filteredDoctors() {
      let result = this.doctors;
      const search = this.doctorSearch.trim().toLowerCase();
      if (search) {
        result = result.filter(doctor => `${doctor.firstname} ${doctor.lastname}`.toLowerCase().includes(search));
      }
      for (const activeFilter of this.filters) {
        if (activeFilter.column === 'state') {
          const filterValues = Array.isArray(activeFilter.value) ? activeFilter.value : [activeFilter.value];
          result = result.filter(doctor => {
            const doctorStates = (doctor.coveredstate || []).map(coveredState => coveredState.state);
            if (activeFilter.operator === 'in') {
              return filterValues.some(state => doctorStates.includes(state));
            }
            if (activeFilter.operator === 'notin') {
              return !filterValues.some(state => doctorStates.includes(state));
            }
            return true;
          });
        }
      }
      return result;
    },
    visibleSelectedCount() {
      return this.filteredDoctors.filter(d => this.selectedIds.includes(d.id)).length;
    },
    headerCheckboxState() {
      if (!this.filteredDoctors.length || this.visibleSelectedCount === 0) {
        return false;
      }
      if (this.visibleSelectedCount === this.filteredDoctors.length) {
        return true;
      }
      return 'indeterminate';
    },
    tableFields() {
      return [{
        key: 'select',
        label: '',
        thClass: 'col-select',
        tdClass: 'col-select align-middle'
      }, {
        key: 'doctor',
        label: 'Doctor',
        tdClass: 'align-middle'
      }, {
        key: 'currentLoad',
        label: 'Current Load',
        thClass: 'text-right',
        tdClass: 'text-right align-middle'
      }, {
        key: 'estimated',
        label: '~Est to Receive',
        thClass: 'text-right',
        tdClass: 'text-right align-middle'
      }, {
        key: 'newTotal',
        label: '~Est New Total',
        thClass: 'text-right',
        tdClass: 'text-right align-middle'
      }];
    },
    bannerStatus() {
      if (!this.sourceDoctor) {
        return null;
      }
      const target = this.reduceTargetCount;
      const assigned = this.totalDistributed;
      if (target === 0) {
        return {
          tone: 'warning',
          message: 'Set a reduction target (percent or count) to preview the distribution.'
        };
      }
      if (this.selectedIds.length === 0) {
        return {
          tone: 'warning',
          message: `Select at least one receiving doctor to distribute the ${target}-patient reduction.`
        };
      }
      if (assigned < target) {
        return {
          tone: 'warning',
          message: `You've allocated ${assigned} of ${target}. Add ${target - assigned} more to another doctor to reach the target.`
        };
      }
      return {
        tone: 'success',
        message: `Ready to transfer. ${target} patients will be reassigned across ${this.selectedIds.length} doctors.`
      };
    },
    footerCaption() {
      if (!this.sourceDoctor) {
        return '';
      }
      if (this.loading || this.loadingLoads) {
        return 'Loading…';
      }
      const target = this.reduceTargetCount;
      if (target === 0) {
        return 'Configure the reduction to preview.';
      }
      const assigned = this.totalDistributed;
      if (assigned === target) {
        return `Ready to reassign ${target} patients across ${this.selectedIds.length} doctors.`;
      }
      return `${assigned} out of ${target} assigned · ${target - assigned} unassigned remaining`;
    },
    distributionComplete() {
      return this.reduceTargetCount > 0 && this.totalDistributed === this.reduceTargetCount;
    },
    changePayload() {
      return {
        ids: this.selectedIds,
        totalDistributed: this.totalDistributed,
        totalIncluded: this.totalIncludedCount,
        includePatients: this.includePatients,
        receivingDoctors: this.receivingDoctorsPayload,
        reduceByMode: this.reduceByMode,
        reduceByAmount: this.reduceByAmount,
        reduceTargetCount: this.reduceTargetCount,
        weightByLoad: this.weightByLoad,
        footerCaption: this.footerCaption,
        distributionComplete: this.distributionComplete
      };
    }
  },
  watch: {
    selectedStates(states) {
      this.selectedIds = [];
      this.doctorSearch = '';
      this.filters = [];
      this.pendingFilters = [];
      this.includePatients = 'ALL';
      this.manualOverrides = {};
      this.doctorScopes = {};
      this.doctorLoads = {};
      this.amountDirty = false;
      this.syncReduceByDefault();
      this.loadDoctors(states);
    },
    eligibilityWindowKey() {
      if (this.selectedStates.length) {
        this.loadDoctors(this.selectedStates);
      }
    },
    includePatients() {
      this.manualOverrides = {};
      this.amountDirty = false;
      this.syncReduceByDefault();
    },
    reduceByMode() {
      this.amountDirty = false;
      this.syncReduceByDefault();
    },
    changePayload: {
      immediate: true,
      handler(val) {
        this.$emit('change', val);
      }
    }
  },
  methods: {
    syncReduceByDefault() {
      if (this.amountDirty) {
        const ceiling = this.reduceByMode === 'PERCENT' ? 100 : this.reduceTargetCeiling;
        if (this.reduceByAmount > ceiling) {
          this.reduceByAmount = ceiling;
        }
        return;
      }
      this.reduceByAmount = this.reduceByMode === 'PERCENT' ? this.sourcePanelTotal > 0 ? Math.round(this.totalIncludedCount / this.sourcePanelTotal * 100) : 0 : this.totalIncludedCount;
    },
    onReduceAmountInput(rawValue) {
      const num = Number(rawValue) || 0;
      const ceiling = this.reduceByMode === 'PERCENT' ? 100 : this.reduceTargetCeiling;
      this.reduceByAmount = Math.max(0, Math.min(num, ceiling));
      this.amountDirty = true;
    },
    onFilterShow() {
      this.openDropdownIndex = null;
      this.pendingFilters = this.filters.map(activeFilter => ({
        ...activeFilter,
        value: Array.isArray(activeFilter.value) ? [...activeFilter.value] : activeFilter.value,
        _stateSearch: ''
      }));
      if (this.pendingFilters.length === 0) {
        this.pendingFilters.push(this.newCondition());
      }
    },
    newCondition() {
      return {
        column: 'state',
        operator: 'in',
        value: [],
        _stateSearch: ''
      };
    },
    addCondition() {
      this.pendingFilters.push(this.newCondition());
    },
    removeCondition(index) {
      this.pendingFilters.splice(index, 1);
    },
    onColumnChange(index) {
      const [firstOperator] = this.getOperators();
      this.pendingFilters[index].operator = firstOperator.value;
      this.pendingFilters[index].value = [];
      this.pendingFilters[index]._stateSearch = '';
    },
    getOperators() {
      return [{
        value: 'in',
        text: 'is any of'
      }, {
        value: 'notin',
        text: 'is none of'
      }];
    },
    applyFilter() {
      this.openDropdownIndex = null;
      this.filters = this.pendingFilters.filter(condition => Array.isArray(condition.value) ? condition.value.length > 0 : condition.value && String(condition.value).trim()).map(condition => ({
        ...condition,
        value: Array.isArray(condition.value) ? [...condition.value] : condition.value
      }));
      this.$refs.filterDropdown.hide();
    },
    cancelFilter() {
      this.openDropdownIndex = null;
      this.$refs.filterDropdown.hide();
    },
    removeFilter(index) {
      this.filters.splice(index, 1);
    },
    clearAllFilters() {
      this.filters = [];
    },
    formatFilterChip(filter) {
      var _this$getOperators$fi;
      const colLabel = (this.columnOptions.find(option => option.value === filter.column) || {}).text || filter.column;
      const opLabel = ((_this$getOperators$fi = this.getOperators().find(option => option.value === filter.operator)) === null || _this$getOperators$fi === void 0 ? void 0 : _this$getOperators$fi.text) || filter.operator;
      const values = Array.isArray(filter.value) ? filter.value : [filter.value];
      const valLabel = values.map(abbr => abbr).join(', ');
      return `${colLabel} ${opLabel}: ${valLabel}`;
    },
    getStateSelectionLabel(condition) {
      const selectedValues = condition.value || [];
      if (!selectedValues.length) {
        return 'Select states...';
      }
      if (selectedValues.length <= 3) {
        return selectedValues.join(', ');
      }
      return `${selectedValues.length} states`;
    },
    getFilteredStateOptions(search) {
      const term = (search || '').trim().toLowerCase();
      if (!term) {
        return this.stateValueOptions;
      }
      return this.stateValueOptions.filter(option => option.value.toLowerCase().includes(term) || option.text.toLowerCase().includes(term));
    },
    toggleStateInCondition(condition, stateAbbr) {
      const currentValues = condition.value || [];
      const existingIndex = currentValues.indexOf(stateAbbr);
      if (existingIndex === -1) {
        condition.value = [...currentValues, stateAbbr];
      } else {
        condition.value = currentValues.filter(abbr => abbr !== stateAbbr);
      }
    },
    async loadDoctors(states) {
      if (!states.length) {
        this.doctors = [];
        return;
      }
      const version = ++this.fetchVersion;
      try {
        var _this$sourceDoctor;
        this.loading = true;
        const {
          data
        } = await DoctorService.getDoctorsByStateV2(states, this.endTimestamp ? this.endTimestamp * 1000 : undefined, this.startTimestamp ? this.startTimestamp * 1000 : undefined, REASSIGNMENT_DISABLED_IN_OPTIONS.GENERAL_PATIENT_TRANSFER, this.reassignmentType);
        if (version !== this.fetchVersion) {
          return;
        }
        const sourceDoctorId = (_this$sourceDoctor = this.sourceDoctor) === null || _this$sourceDoctor === void 0 ? void 0 : _this$sourceDoctor.id;
        this.doctors = (Array.isArray(data) ? data : []).filter(doctor => {
          var _doctor$reassignment_, _doctor$reassignment_2;
          return doctor.id !== sourceDoctorId && (doctor.override_reassignment_rule || !((_doctor$reassignment_ = doctor.reassignment_disabled_in) !== null && _doctor$reassignment_ !== void 0 && _doctor$reassignment_.includes(REASSIGNMENT_DISABLED_IN_OPTIONS.GENERAL_PATIENT_TRANSFER)) && (this.reassignmentType !== REASSIGNMENT_TYPE.PERMANENT || !((_doctor$reassignment_2 = doctor.reassignment_disabled_in) !== null && _doctor$reassignment_2 !== void 0 && _doctor$reassignment_2.includes(REASSIGNMENT_DISABLED_IN_OPTIONS.PERMANENT_REASSIGNMENT))));
        }).map(doctor => ({
          ...doctor,
          _showDetails: false
        }));
        this.pruneSelectionsToEligible();
        this.loadDoctorLoads(this.doctors, version);
      } catch {
        if (version !== this.fetchVersion) {
          return;
        }
        this.$emit('error', 'Failed to load covering doctors.');
        this.doctors = [];
      } finally {
        if (version === this.fetchVersion) {
          this.loading = false;
        }
      }
    },
    async loadDoctorLoads(doctorList, version) {
      if (!doctorList.length) {
        this.doctorLoads = {};
        return;
      }
      this.loadingLoads = true;
      try {
        const results = await Promise.all(doctorList.map(doctor => DoctorService.getPatientCountByDoctor(doctor.id).then(({
          data
        }) => [doctor.id, data]).catch(() => [doctor.id, null])));
        if (version !== this.fetchVersion) {
          return;
        }
        const loads = {};
        for (const [id, stateCounts] of results) {
          loads[id] = (Array.isArray(stateCounts) ? stateCounts : []).reduce((sum, stateCount) => sum + Number(stateCount.active || 0), 0);
        }
        this.doctorLoads = loads;
      } finally {
        if (version === this.fetchVersion) {
          this.loadingLoads = false;
        }
      }
    },
    getRelevantStates(doctor) {
      if (!Array.isArray(doctor === null || doctor === void 0 ? void 0 : doctor.coveredstate)) {
        return [];
      }
      const doctorStates = doctor.coveredstate.map(cs => cs.state);
      return this.selectedStates.filter(abbr => doctorStates.includes(abbr));
    },
    getDisplayedStates(doctor) {
      if (this.selectedIds.includes(doctor.id)) {
        return this.doctorScopes[doctor.id] || [];
      }
      return this.getRelevantStates(doctor);
    },
    getVisibleBadges(doctor) {
      return this.getDisplayedStates(doctor).slice(0, 6);
    },
    getOverflowBadges(doctor) {
      return this.getDisplayedStates(doctor).slice(6);
    },
    getOverflowCount(doctor) {
      return Math.max(0, this.getDisplayedStates(doctor).length - 6);
    },
    toggleRow(item) {
      item._showDetails = !item._showDetails;
    },
    pruneSelectionsToEligible() {
      const eligibleIds = new Set(this.doctors.map(d => d.id));
      const staleIds = this.selectedIds.filter(id => !eligibleIds.has(id));
      if (!staleIds.length) {
        return;
      }
      this.selectedIds = this.selectedIds.filter(id => eligibleIds.has(id));
      staleIds.forEach(id => {
        Vue.delete(this.doctorScopes, id);
        Vue.delete(this.manualOverrides, id);
      });
    },
    toggleDoctor(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) {
        this.selectedIds = [...this.selectedIds, id];
        const doctor = this.doctors.find(d => d.id === id);
        Vue.set(this.doctorScopes, id, this.getRelevantStates(doctor));
      } else {
        this.selectedIds = this.selectedIds.filter(sid => sid !== id);
        Vue.delete(this.doctorScopes, id);
        Vue.delete(this.manualOverrides, id);
      }
    },
    onHeaderCheckboxChange(checked) {
      const visibleIds = this.filteredDoctors.map(d => d.id);
      if (!checked) {
        visibleIds.forEach(id => {
          if (this.selectedIds.includes(id)) {
            this.toggleDoctor(id);
          }
        });
        return;
      }
      visibleIds.forEach(id => {
        if (!this.selectedIds.includes(id)) {
          this.toggleDoctor(id);
        }
      });
      let prunedZeroShareDoctor = true;
      while (prunedZeroShareDoctor) {
        prunedZeroShareDoctor = false;
        visibleIds.forEach(id => {
          if (this.selectedIds.includes(id) && !(id in this.manualOverrides) && this.getTotalEstimate(id) === 0) {
            this.toggleDoctor(id);
            prunedZeroShareDoctor = true;
          }
        });
      }
    },
    openScopePopover(doctorId) {
      this.scopePopoverDoctorId = this.scopePopoverDoctorId === doctorId ? null : doctorId;
    },
    toggleScopeState(doctorId, stateAbbr) {
      const current = this.doctorScopes[doctorId] || [];
      const next = current.includes(stateAbbr) ? current.filter(abbr => abbr !== stateAbbr) : [...current, stateAbbr];
      Vue.set(this.doctorScopes, doctorId, next);
    },
    setScopeAll(doctor) {
      Vue.set(this.doctorScopes, doctor.id, this.getRelevantStates(doctor));
    },
    setScopeNone(doctorId) {
      Vue.set(this.doctorScopes, doctorId, []);
    },
    closeScopePopover(doctorId) {
      this.scopePopoverDoctorId = null;
      if (!(this.doctorScopes[doctorId] || []).length) {
        this.toggleDoctor(doctorId);
      }
    },
    getDoctorWeight(doctorId, eligibleIds) {
      if (!this.weightByLoad) {
        return 1;
      }
      const loads = eligibleIds.map(id => this.doctorLoads[id] || 0);
      const maxLoad = Math.max(0, ...loads);
      return Math.max(1, maxLoad - (this.doctorLoads[doctorId] || 0) + 1);
    },
    computeAutoAllocation(overridesMap) {
      const overriddenIds = Object.keys(overridesMap).filter(id => this.selectedIds.includes(id));
      const overrideTotal = overriddenIds.reduce((sum, id) => sum + (overridesMap[id] || 0), 0);
      const autoIds = this.selectedIds.filter(id => !overriddenIds.includes(id));
      const autoTarget = Math.max(0, this.reduceTargetCount - overrideTotal);
      const entries = this.stateCountsFiltered;
      const stateShares = splitByWeights(autoTarget, entries.map(e => e.count));
      const result = {};
      autoIds.forEach(id => {
        result[id] = {};
      });
      entries.forEach((entry, index) => {
        const stateAllocated = stateShares[index];
        if (stateAllocated <= 0) {
          return;
        }
        const eligibleIds = autoIds.filter(id => (this.doctorScopes[id] || []).includes(entry.abbr));
        if (!eligibleIds.length) {
          return;
        }
        const denom = entry.ownedTotal + entry.coveringTotal;
        const ownedPool = denom > 0 ? Math.round(stateAllocated * entry.ownedTotal / denom) : stateAllocated;
        const coveringPool = stateAllocated - ownedPool;
        const weights = eligibleIds.map(id => this.getDoctorWeight(id, eligibleIds));
        const ownedShares = splitByWeights(ownedPool, weights);
        const coveringShares = splitByWeights(coveringPool, weights);
        eligibleIds.forEach((id, i) => {
          if (ownedShares[i] > 0 || coveringShares[i] > 0) {
            result[id][entry.abbr] = {
              owned: ownedShares[i],
              covering: coveringShares[i]
            };
          }
        });
      });
      return result;
    },
    getStateBreakdown(doctorId) {
      if (!this.selectedIds.includes(doctorId)) {
        return [];
      }
      if (doctorId in this.manualOverrides) {
        const total = this.manualOverrides[doctorId];
        const scope = this.doctorScopes[doctorId] || [];
        const totals = scope.reduce((acc, abbr) => {
          const entry = this.stateCountsFiltered.find(e => e.abbr === abbr);
          if (entry) {
            acc.owned += entry.ownedTotal;
            acc.covering += entry.coveringTotal;
          }
          return acc;
        }, {
          owned: 0,
          covering: 0
        });
        const denom = totals.owned + totals.covering;
        const owned = denom > 0 ? Math.round(total * totals.owned / denom) : total;
        return [{
          state: 'Manual',
          share: total,
          owned,
          covering: total - owned
        }];
      }
      const perState = this.autoAllocation[doctorId] || {};
      return Object.entries(perState).map(([state, {
        owned,
        covering
      }]) => ({
        state,
        share: owned + covering,
        owned,
        covering
      })).filter(line => line.share > 0);
    },
    getDoctorOwnedCovering(doctorId) {
      const breakdown = this.getStateBreakdown(doctorId);
      return {
        owned: breakdown.reduce((sum, l) => sum + l.owned, 0),
        covering: breakdown.reduce((sum, l) => sum + l.covering, 0)
      };
    },
    getTotalEstimate(doctorId) {
      if (doctorId in this.manualOverrides) {
        return this.manualOverrides[doctorId];
      }
      return this.getStateBreakdown(doctorId).reduce((sum, breakdown) => sum + breakdown.share, 0);
    },
    getDeltaNote(doctorId) {
      if (doctorId in this.manualOverrides || !Object.keys(this.manualOverrides).length) {
        return null;
      }
      const live = this.getTotalEstimate(doctorId);
      const baselinePerState = this.baselineAllocation[doctorId] || {};
      const baseline = Object.values(baselinePerState).reduce((sum, l) => sum + l.owned + l.covering, 0);
      const delta = live - baseline;
      if (Math.abs(delta) < 3) {
        return null;
      }
      return delta > 0 ? `↑${delta} vs auto` : `↓${Math.abs(delta)} vs auto`;
    },
    loadPercentLabel(doctorId) {
      const load = this.doctorLoads[doctorId] || 0;
      if (load <= 0) {
        return 0;
      }
      return Math.round(this.getTotalEstimate(doctorId) / load * 100);
    },
    loadPercentClass(doctorId) {
      const percent = this.loadPercentLabel(doctorId);
      if (percent >= 100) {
        return 'tw-text-red-600 tw-font-semibold';
      }
      if (percent >= 30) {
        return 'tw-text-orange-500 tw-font-semibold';
      }
      return 'text-muted';
    },
    getSharingLabel(stateAbbr) {
      const sharingCount = this.selectedIds.filter(id => {
        return this.getStateBreakdown(id).some(l => l.state === stateAbbr && l.share > 0);
      }).length;
      return sharingCount <= 1 ? 'sole' : `split ${sharingCount}`;
    },
    setManualOverride(doctorId, value) {
      const othersTotal = this.selectedIds.filter(id => id !== doctorId && id in this.manualOverrides).reduce((sum, id) => sum + this.manualOverrides[id], 0);
      const ceiling = Math.max(0, this.reduceTargetCount - othersTotal);
      Vue.set(this.manualOverrides, doctorId, Math.max(0, Math.min(value, ceiling)));
    },
    onNudge(doctorId, delta) {
      this.setManualOverride(doctorId, this.getTotalEstimate(doctorId) + delta);
    },
    onManualInput(doctorId, value) {
      this.setManualOverride(doctorId, Number(value) || 0);
    }
  }
};